import { useMemo, useEffect, useReducer } from 'react';
import {
    getLocalStorageItem,
    LocalStorageChangeEvent,
    LocalStorageItem,
    removeLocalStorageItem,
    setLocalStorageItem,
} from '../utils/localStorage';

/**
 * Represents a wrapper around a key in localStorage.
 *
 * @template T - The type of the stored value.
 */
type LocalStorageState<T> = [
    LocalStorageItem<T>, // The current value of the key
    (value: T) => void, // Sets a new value for the key
    () => void, // Removes the key from storage
];

/**
 * Returns a stateful value stored in the localStorage with the given key.
 * If no value is found, the defaultValue parameter is returned.
 *
 * @template T - The type of the value being stored in the localStorage
 * @param {string} key - The key used to store the value in the localStorage
 * @param {T | null} [defaultValue=null] - The default value to be returned if no value is found in the localStorage
 */
export default function useLocalStorage<T>(
    key: string,
    defaultValue: T | null = null
): LocalStorageState<T> {
    // `id` changes every time a change in the `localStorage` occurs
    const [id, forceUpdate] = useReducer((number) => number + 1, 0);

    // Handles events and syncing
    useEffect(() => {
        const onStorage = (event: StorageEvent | LocalStorageChangeEvent) => {
            const isValidStorageEvent = () =>
                event instanceof StorageEvent &&
                event.storageArea === localStorage &&
                event.key === key;
            const isValidLocalStorageChangeEvent = () =>
                event instanceof LocalStorageChangeEvent &&
                event.detail.key === key;
            if (isValidStorageEvent() || isValidLocalStorageChangeEvent()) {
                forceUpdate();
            }
        };
        // Syncs change in tabs, windows, and frames other than the triggering context.
        window.addEventListener('storage', onStorage);

        // Syncs change in tabs, windows, and frames other than the triggering context.
        window.addEventListener('localStorageChange', onStorage);

        return (): void => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('localStorageChange', onStorage);
        };
    }, [key]);

    return useMemo(
        () => [
            getLocalStorageItem<T>(key) ?? defaultValue,
            (value: T) => setLocalStorageItem<T>(key, value),
            () => {
                removeLocalStorageItem(key);
            },
        ],
        [id, key]
    );
}
